import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
} from "react";
import axios from "axios";

export const ThemeDataContext = createContext(null);

// Default hardcoded theme configuration
const defaultThemeConfig = {
  // Brand Colors
  primary: "#6366F1",
  secondary: "#8B5CF6",
  accent: "#06B6D4",
  success: "#10B981",
  warning: "#F59E0B",
  error: "#EF4444",
  info: "#3B82F6",

  // Background Colors
  background_default_light: "#F9FAFB",
  background_paper_light: "#FFFFFF",
  background_default_dark: "#0F172A",
  background_paper_dark: "#1E293B",

  // Text Colors
  text_primary_light: "#1F2937",
  text_secondary_light: "#6B7280",
  text_disabled_light: "#9CA3AF",
  text_primary_dark: "#F9FAFB",
  text_secondary_dark: "#CBD5E1",
  text_disabled_dark: "#64748B",

  // UI Element Colors
  divider_light: "#E5E7EB",
  divider_dark: "#334155",
  border_light: "#D1D5DB",
  border_dark: "#475569",

  // ========== MUI BUTTON CUSTOMIZATION ==========
  button: {
    // Contained Button
    contained: {
      borderRadius: 12,
      padding: "10px 24px",
      fontSize: "0.9375rem",
      fontWeight: 600,
      textTransform: "none",
      boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
      hoverBoxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
      hoverTransform: "translateY(-2px)",
      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    },
    // Outlined Button
    outlined: {
      borderRadius: 12,
      padding: "10px 24px",
      fontSize: "0.9375rem",
      fontWeight: 600,
      textTransform: "none",
      borderWidth: "1.5px",
      hoverBorderWidth: "2px",
      hoverBgOpacity: 0.08,
    },
    // Text Button
    text: {
      borderRadius: 12,
      padding: "10px 16px",
      fontSize: "0.9375rem",
      fontWeight: 600,
      textTransform: "none",
      hoverBgOpacity: 0.08,
    },
    // Size variants
    small: {
      padding: "6px 16px",
      fontSize: "0.8125rem",
    },
    medium: {
      padding: "10px 24px",
      fontSize: "0.9375rem",
    },
    large: {
      padding: "14px 32px",
      fontSize: "1rem",
    },
  },

  // ========== MUI CARD CUSTOMIZATION ==========
  card: {
    borderRadius: 16,
    padding: 24,
    boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
    hoverBoxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
    hoverTransform: "translateY(-4px)",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    border: "none",
    borderColor_light: "#E5E7EB",
    borderColor_dark: "#334155",
  },

  // ========== MUI BOX CUSTOMIZATION ==========
  box: {
    defaultBorderRadius: 12,
    defaultPadding: 16,
    defaultMargin: 0,
  },

  // ========== MUI PAPER CUSTOMIZATION ==========
  paper: {
    borderRadius: 16,
    elevation0: "none",
    elevation1: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
    elevation2: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
    elevation3: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
    elevation4: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
  },

  // ========== MUI TEXTFIELD/INPUT CUSTOMIZATION ==========
  textField: {
    borderRadius: 10,
    padding: "12px 16px",
    fontSize: "1rem",
    borderColor_light: "#D1D5DB",
    borderColor_dark: "#475569",
    focusBorderColor: "#6366F1",
    focusBorderWidth: "2px",
    hoverBorderColor: "#6366F1",
    errorBorderColor: "#EF4444",
    backgroundColor_light: "#FFFFFF",
    backgroundColor_dark: "#1E293B",
  },

  // ========== MUI CHIP CUSTOMIZATION ==========
  chip: {
    borderRadius: 8,
    padding: "4px 12px",
    fontSize: "0.875rem",
    fontWeight: 500,
    height: 32,
    deleteIconSize: 18,
  },

  // ========== MUI DIALOG CUSTOMIZATION ==========
  dialog: {
    borderRadius: 20,
    padding: 24,
    maxWidth: "sm",
    backdropBlur: "8px",
    backdropOpacity: 0.5,
  },

  // ========== MUI APPBAR/HEADER CUSTOMIZATION ==========
  appBar: {
    height: 64,
    padding: "0 24px",
    boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
    backgroundColor_light: "#FFFFFF",
    backgroundColor_dark: "#1E293B",
    borderBottom: "none",
  },

  // ========== MUI DRAWER/SIDEBAR CUSTOMIZATION ==========
  drawer: {
    width: 280,
    borderRadius: 0,
    backgroundColor_light: "#FFFFFF",
    backgroundColor_dark: "#1E293B",
    itemPadding: "12px 16px",
    itemBorderRadius: 8,
    itemHoverBg_light: "#F3F4F6",
    itemHoverBg_dark: "#334155",
    itemActiveBg_light: "#EEF2FF",
    itemActiveBg_dark: "#312E81",
    itemActiveColor: "#6366F1",
  },

  // ========== MUI TOOLTIP CUSTOMIZATION ==========
  tooltip: {
    borderRadius: 8,
    padding: "8px 12px",
    fontSize: "0.875rem",
    backgroundColor_light: "#1F2937",
    backgroundColor_dark: "#F9FAFB",
    color_light: "#FFFFFF",
    color_dark: "#1F2937",
    maxWidth: 300,
    arrow: true,
  },

  // ========== MUI ALERT CUSTOMIZATION ==========
  alert: {
    borderRadius: 12,
    padding: "12px 16px",
    fontSize: "0.9375rem",
    fontWeight: 500,
    iconSize: 22,
  },

  // ========== MUI SNACKBAR CUSTOMIZATION ==========
  snackbar: {
    borderRadius: 12,
    padding: "12px 16px",
    fontSize: "0.9375rem",
    maxWidth: 600,
    autoHideDuration: 6000,
  },

  // ========== MUI TABLE CUSTOMIZATION ==========
  table: {
    headerBg_light: "#F9FAFB",
    headerBg_dark: "#1E293B",
    headerFontWeight: 600,
    headerFontSize: "0.875rem",
    rowHoverBg_light: "#F9FAFB",
    rowHoverBg_dark: "#334155",
    cellPadding: "16px",
    borderColor_light: "#E5E7EB",
    borderColor_dark: "#334155",
  },

  // ========== MUI TABS CUSTOMIZATION ==========
  tabs: {
    indicatorColor: "#6366F1",
    indicatorHeight: 3,
    tabPadding: "12px 24px",
    tabFontSize: "0.9375rem",
    tabFontWeight: 600,
    tabMinHeight: 48,
  },

  // ========== MUI ACCORDION CUSTOMIZATION ==========
  accordion: {
    borderRadius: 12,
    padding: "16px",
    boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
    expandIconColor: "#6B7280",
  },

  // ========== MUI MENU CUSTOMIZATION ==========
  menu: {
    borderRadius: 12,
    padding: "8px",
    boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
    itemPadding: "10px 16px",
    itemBorderRadius: 8,
    itemFontSize: "0.9375rem",
  },

  // ========== MUI SWITCH CUSTOMIZATION ==========
  switch: {
    width: 42,
    height: 24,
    thumbSize: 18,
    trackBorderRadius: 12,
    checkedColor: "#6366F1",
    uncheckedColor: "#D1D5DB",
  },

  // ========== MUI CHECKBOX CUSTOMIZATION ==========
  checkbox: {
    size: 20,
    borderRadius: 4,
    checkedColor: "#6366F1",
    borderWidth: 2,
  },

  // ========== MUI RADIO CUSTOMIZATION ==========
  radio: {
    size: 20,
    checkedColor: "#6366F1",
    borderWidth: 2,
  },

  // ========== MUI SLIDER CUSTOMIZATION ==========
  slider: {
    height: 4,
    thumbSize: 16,
    trackColor: "#6366F1",
    railColor: "#E5E7EB",
    borderRadius: 4,
  },

  // ========== MUI AVATAR CUSTOMIZATION ==========
  avatar: {
    small: 32,
    medium: 40,
    large: 56,
    borderRadius: "50%",
    border: "none",
  },

  // ========== MUI BADGE CUSTOMIZATION ==========
  badge: {
    fontSize: "0.75rem",
    fontWeight: 600,
    height: 20,
    minWidth: 20,
    padding: "0 6px",
    borderRadius: 10,
  },

  // ========== MUI BREADCRUMBS CUSTOMIZATION ==========
  breadcrumbs: {
    fontSize: "0.875rem",
    separator: "/",
    separatorColor: "#9CA3AF",
  },

  // ========== MUI STEPPER CUSTOMIZATION ==========
  stepper: {
    connectorHeight: 2,
    connectorColor: "#E5E7EB",
    activeColor: "#6366F1",
    completedColor: "#10B981",
    iconSize: 24,
  },

  // ========== MUI PAGINATION CUSTOMIZATION ==========
  pagination: {
    borderRadius: 8,
    itemSize: 32,
    fontSize: "0.875rem",
    selectedBg: "#6366F1",
    selectedColor: "#FFFFFF",
    hoverBg: "#F3F4F6",
  },

  // ========== MUI DIVIDER CUSTOMIZATION ==========
  divider: {
    thickness: 1,
    color_light: "#E5E7EB",
    color_dark: "#334155",
    margin: "16px 0",
  },

  // ========== MUI LIST CUSTOMIZATION ==========
  list: {
    itemPadding: "12px 16px",
    itemBorderRadius: 8,
    itemHoverBg_light: "#F9FAFB",
    itemHoverBg_dark: "#334155",
    itemSelectedBg_light: "#EEF2FF",
    itemSelectedBg_dark: "#312E81",
  },

  // Typography
  font_family:
    '"Inter", "Outfit", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
  font_size_base: 16,
  font_weight_light: 300,
  font_weight_regular: 400,
  font_weight_medium: 500,
  font_weight_semibold: 600,
  font_weight_bold: 700,

  // Spacing
  spacing_unit: 8,

  // Animations
  transition_duration: "0.2s",
  transition_easing: "cubic-bezier(0.4, 0, 0.2, 1)",

  // Gradients
  gradient_primary: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
  gradient_secondary: "linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)",
  gradient_success: "linear-gradient(135deg, #10B981 0%, #059669 100%)",

  // Glass morphism effects
  glass_bg_light: "rgba(255, 255, 255, 0.7)",
  glass_bg_dark: "rgba(30, 41, 59, 0.7)",
  glass_blur: "10px",

  // AI/Tech specific colors
  ai_accent_1: "#A78BFA",
  ai_accent_2: "#34D399",
  ai_accent_3: "#60A5FA",
  ai_glow: "0 0 20px rgba(99, 102, 241, 0.3)",
};

export const ThemeDataProvider = ({ children }) => {
  const [themeConfig, setThemeConfig] = useState(defaultThemeConfig);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState(() => {
    const saved = localStorage.getItem("theme_mode");
    if (saved === "dark" || saved === "light") return saved;
    return window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  });

  const resetThemeToDefault = async () => {
    try {
      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/theme/reset-theme-config`,
      );
      window.location.reload();
    } catch (error) {
      setThemeConfig(defaultThemeConfig);
    }
  };

  // Clean up the useEffect — remove the savedMode block entirely
  useEffect(() => {
    const fetchTheme = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/api/theme/get-theme-config`,
        );
        if (response.data?.success && response.data.data) {
          try {
            const mergedConfig = deepMerge(
              defaultThemeConfig,
              response.data.data,
            );
            setThemeConfig(mergedConfig);
          } catch (mergeError) {
            await resetThemeToDefault();
          }
        }
      } catch (error) {
        console.log("Failed to fetch theme, using defaults:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTheme();
    // ← savedMode block removed, useState lazy init handles it
  }, []);

  // ✨ Animated toggle — ripple expands from button position
  const toggleColorMode = (originX, originY) => {
    const newMode = mode === "light" ? "dark" : "light";

    // Fallback: no animation support
    if (!document.startViewTransition) {
      setMode(newMode);
      localStorage.setItem("theme_mode", newMode);
      return;
    }

    // Calculate max radius to cover full screen
    const maxRadius = Math.hypot(
      Math.max(originX, window.innerWidth - originX),
      Math.max(originY, window.innerHeight - originY),
    );

    document
      .startViewTransition(() => {
        setMode(newMode);
        localStorage.setItem("theme_mode", newMode);
      })
      .ready.then(() => {
        document.documentElement.animate(
          {
            clipPath: [
              `circle(0px at ${originX}px ${originY}px)`,
              `circle(${maxRadius}px at ${originX}px ${originY}px)`,
            ],
          },
          {
            duration: 500,
            easing: "ease-in-out",
            pseudoElement: "::view-transition-new(root)",
          },
        );
      });
  };

  const value = {
    themeConfig,
    setThemeConfig,
    loading,
    mode,
    toggleColorMode,
    defaultThemeConfig,
    resetThemeToDefault,
  };

  return (
    <ThemeDataContext.Provider value={value}>
      {children}
    </ThemeDataContext.Provider>
  );
};

// Deep merge utility function with error handling
function deepMerge(target, source) {
  try {
    const output = { ...target };

    if (isObject(target) && isObject(source)) {
      Object.keys(source).forEach((key) => {
        if (isObject(source[key])) {
          if (!(key in target)) {
            output[key] = source[key];
          } else {
            output[key] = deepMerge(target[key], source[key]);
          }
        } else {
          output[key] = source[key];
        }
      });
    }

    return output;
  } catch (error) {
    console.error("Deep merge error:", error);
    throw error;
  }
}

function isObject(item) {
  return item && typeof item === "object" && !Array.isArray(item);
}

export const useThemeData = () => {
  const context = useContext(ThemeDataContext);
  if (!context) {
    throw new Error("useThemeData must be used within ThemeDataProvider");
  }
  return context;
};
